/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { styled, useTheme } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import * as _ from "lodash";
import BaseButton from "./common/BaseButton";
import StyledNoResults from "./common/StyledNoResults";
import { getTierSettings, putManageDelaySettings } from "../api/api";
import Loader from "../components/Loader";
import StyledTextField from "../components/common/StyledTextField";
import Notification from "./common/Notification";

const StyledGridWrap = styled(Grid)({
  padding: "20px 0 200px 20px",
  flex: "1 0 auto",
});
const StyledGridRow = styled(Grid)({ margin: "10px 0 0 0" });
const StyledTypography = styled(Typography)({
  margin: "0 10px 0 0",
});
const StyledDivider = styled(Divider)({ margin: "10px 0 10px 0" });

const TierSettings = () => {
  const theme = useTheme();
  const userId = useSelector((state) => state.goTrackUser.goTrackUser.id);
  const [isFetched, setIsFetched] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchedTiers, setFetchedTiers] = useState([]);
  const [operativeTiers, setOperativeTiers] = useState([]);
  const [tier1, setTier1] = useState(0);
  const [tier2, setTier2] = useState(0);
  const [tier3, setTier3] = useState(0);
  const [
    showTierSettingsSuccessNotification,
    setShowTierSettingsSuccessNotification,
  ] = useState(false);
  const [
    showTierSettingsFailureNotification,
    setShowTierSettingsFailureNotification,
  ] = useState(false);

  const [disabled, setDisabled] = useState(true);

  const validateNumber = (value) => {
    if (isNaN(value)) {
      value = value.substring(0, value.length - 1);
    }

    if (value !== "") {
      value = parseInt(value);
    }
    return value;
  };

  const tierHandleChange = (value, setter, rank) => {
    value = validateNumber(value);
    setter(value);
    const tier = operativeTiers.find((t) => t.rank === rank);
    tier.delayInMinutes = value;

    setDisabled(IsDataNotChanged());
  };

  const IsDataNotChanged = () => {
    let isDataNotChanged = true;
    for (const operativeTier of operativeTiers) {
      let fetchedTier = fetchedTiers.find(
        (ft) => ft.rank === operativeTier.rank
      );

      if (fetchedTier.delayInMinutes !== operativeTier.delayInMinutes) {
        isDataNotChanged = false;
        break;
      }
    }
    return isDataNotChanged;
  };

  const saveChanges = async () => {
    let isSavingSuccessful = true;

    for (const operativeTier of operativeTiers) {
      let fetchedTier = fetchedTiers.find(
        (ft) => ft.rank === operativeTier.rank
      );

      if (fetchedTier.delayInMinutes !== operativeTier.delayInMinutes) {
        try {
          await putManageDelaySettings(
            operativeTier.tiersId,
            operativeTier.delayInMinutes,
            userId,
            new Date().toLocaleString()
          );
          fetchedTier.delayInMinutes = operativeTier.delayInMinutes;
        } catch (e) {
          isSavingSuccessful = false;
        }
      }
    }

    if (isSavingSuccessful) {
      setShowTierSettingsSuccessNotification(true);
      setDisabled(true);
    } else {
      setShowTierSettingsFailureNotification(true);
    }
  };

  const getDelayInMinutes = (tierSettings, rank) => {
    const tierSetting = tierSettings.find((t) => t.rank === rank);
    return tierSetting !== undefined ? tierSetting.delayInMinutes : 0;
  };

  const fetchTierSettings = async () => {
    const tierSettings = await getTierSettings();
    setIsFetched(true);
    setHasFetched(true);
    setFetchedTiers(tierSettings);
    setOperativeTiers(_.cloneDeep(tierSettings));

    const tier1DelayInMinutes = getDelayInMinutes(tierSettings, 1);
    setTier1(tier1DelayInMinutes);

    const tier2DelayInMinutes = getDelayInMinutes(tierSettings, 2);
    setTier2(tier2DelayInMinutes);

    const tier3DelayInMinutes = getDelayInMinutes(tierSettings, 3);
    setTier3(tier3DelayInMinutes);
  };

  useEffect(() => {
    try {
      fetchTierSettings();
    } catch (e) {
      setIsFetched(false);
    }
  }, []);

  return (
    <>
      {isFetched ? (
        <StyledGridWrap container xs={6}>
          <StyledTypography xs={6} variant="h5">
            Tier 1
          </StyledTypography>
          <StyledGridRow container justify="flex-start" alignItems="center">
            <StyledTypography variant="h7">
              Delay time to view available legs:
            </StyledTypography>
            <StyledTextField
              variant="outlined"
              value={tier1}
              onChange={(event) =>
                tierHandleChange(event.target.value, setTier1, 1)
              }
              onBlur={() => {
                if (tier1 === "") {
                  tierHandleChange(0, setTier1, 1);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
            />
          </StyledGridRow>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <StyledTypography variant="h5" xs={6}>
            Tier 2
          </StyledTypography>
          <StyledGridRow container justify="flex-start" alignItems="center">
            <StyledTypography variant="h7">
              Delay time to view available legs:
            </StyledTypography>
            <StyledTextField
              variant="outlined"
              value={tier2}
              onChange={(event) =>
                tierHandleChange(event.target.value, setTier2, 2)
              }
              onBlur={() => {
                if (tier2 === "") {
                  tierHandleChange(0, setTier2, 2);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
            />
          </StyledGridRow>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <StyledTypography variant="h5" xs={6}>
            Tier 3
          </StyledTypography>
          <StyledGridRow container justify="flex-start" alignItems="center">
            <StyledTypography variant="h7">
              Delay time to view available legs:
            </StyledTypography>
            <StyledTextField
              variant="outlined"
              value={tier3}
              onChange={(event) =>
                tierHandleChange(event.target.value, setTier3, 3)
              }
              onBlur={() => {
                if (tier3 === "") {
                  tierHandleChange(0, setTier3, 3);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
            />
          </StyledGridRow>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <StyledTypography variant="h5" xs={6}>
            Do Not Use
          </StyledTypography>
          <StyledGridRow container justify="flex-start" alignItems="center">
            <StyledTypography variant="h7">
              "Do Not Use" carriers will never see posted legs.
            </StyledTypography>
          </StyledGridRow>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid container justify="flex-end">
            <BaseButton
              onClick={saveChanges}
              disabled={disabled}
              data-testid="btn-save-changes"
            >
              Save changes
            </BaseButton>
          </Grid>
        </StyledGridWrap>
      ) : (
        hasFetched ? <StyledNoResults theme={theme}>No Results</StyledNoResults> : <Loader/>
      )}

      <Notification
        message="Tier settings updated successfully."
        open={showTierSettingsSuccessNotification}
        setOpen={setShowTierSettingsSuccessNotification}
      />
      <Notification
        message="Attempt to update Tier setting failure."
        open={showTierSettingsFailureNotification}
        setOpen={setShowTierSettingsFailureNotification}
      />
    </>
  );
};

export default TierSettings;
